import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchGenders,
  fetchCountries,
  fetchNationalities,
  validateNicDob,
  validateNicGender,
  fetchDistricts,
  validateCityZip,
  validateCityDistrict,
  checkGuideRegistrationLimit,
  submitAgentLimitIncreaseRequest,
} from "../services/agentService";
import axios from "axios";
import Swal from "sweetalert2";
import { FaArrowLeft, FaIdCard, FaFileAlt, FaCheckCircle, FaCloudUploadAlt, FaMapMarkerAlt, FaUser, FaGlobe } from "react-icons/fa";
import "./Register.css";
import NavUser from "../components/NavUser";

const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => localStorage.getItem("jwt_token");

function AgentGuideRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isAgentApproved, setIsAgentApproved] = useState(true);
  const [approvalMessage, setApprovalMessage] = useState("");

  // dropdown lists
  const [genders, setGenders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [districts, setDistricts] = useState([]);

  // form
  const [form, setForm] = useState({
    full_name: "",
    phone_number: "",
    email: "",
    national_id_number: "",
    dob: "",
    country_id: "",
    nationality_id: "",
    gender_id: "",
    address: "",
    city: "",
    zip_code: "",
    district_id: "",
  });

  // files
  const [nicFront, setNicFront] = useState(null);
  const [nicBack, setNicBack] = useState(null);
  const [waterFront, setWaterFront] = useState(null);
  const [waterBack, setWaterBack] = useState(null);
  const [businessDoc, setBusinessDoc] = useState(null);

  const handleFile = (setter) => (e) => setter(e.target.files[0]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  useEffect(() => {
    const loadData = async () => {
      try {
        const limitCheck = await checkGuideRegistrationLimit().catch(() => null);

        if (limitCheck && !limitCheck.allowed && limitCheck.message?.toLowerCase().includes("pending")) {
          setIsAgentApproved(false);
          setApprovalMessage(limitCheck.message);
        }

        setGenders(await fetchGenders());
        const allCountries = await fetchCountries();
        const slCountries = allCountries.filter((c) =>
          c.name.toLowerCase().includes("sri lanka")
        );
        setCountries(slCountries);

        const allNats = await fetchNationalities();
        const slNats = allNats.filter((n) =>
          n.name.toLowerCase().includes("sri lanka")
        );
        setNationalities(slNats);

        setDistricts(await fetchDistricts());

        setForm((prev) => ({
          ...prev,
          country_id: slCountries[0]?.id || "",
          nationality_id: slNats[0]?.id || prev.nationality_id,
        }));
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAgentApproved) {
      Swal.fire({
        title: "Account Pending",
        text: approvalMessage || "Your agent account is pending administrator approval.",
        icon: "warning"
      });
      return;
    }

    try {
      setLoading(true);

      const limitCheck = await checkGuideRegistrationLimit();

      if (!limitCheck.allowed) {
        setLoading(false);
        if (limitCheck.hasPendingRequest) {
          Swal.fire({
            title: "Request Under Review",
            text: "You have reached your guide registration limit. Your request for a limit increase is currently under administrator review.",
            icon: "info"
          });
          return;
        }

        const promptResult = await Swal.fire({
          title: "Limit Reached",
          text: limitCheck.message || `You have reached your guide registration limit (${limitCheck.limit}). Would you like to request an increase?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Request Limit Increase",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#2563eb",
        });

        if (promptResult.isConfirmed) {
          const { value: reason } = await Swal.fire({
            title: "Request Guide Limit Increase",
            input: "textarea",
            inputLabel: "Please provide a reason for requesting more guide registrations:",
            inputPlaceholder: "e.g., Onboarding local guides for cultural tours in Sigiriya...",
            showCancelButton: true,
            confirmButtonText: "Submit Request",
            confirmButtonColor: "#2563eb",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "Please enter a reason.";
            },
          });

          if (reason) {
            try {
              await submitAgentLimitIncreaseRequest("guide", reason.trim());
              Swal.fire("Request Submitted", "Your limit increase request has been sent to the administrator.", "success");
            } catch (err) {
              Swal.fire("Error", err.response?.data?.error || "Failed to submit request.", "error");
            }
          }
        }
        return;
      }

      const nic = form.national_id_number;
      const dob = form.dob;

      const isNicDobValid = await validateNicDob(nic, dob);
      if (!isNicDobValid) {
        Swal.fire({ title: "Validation Error", text: "NIC does not match Date of Birth.", icon: "error" });
        setLoading(false);
        return;
      }

      const isNicGenderValid = await validateNicGender(nic, Number(form.gender_id));
      if (!isNicGenderValid) {
        Swal.fire({ title: "Validation Error", text: "NIC does not match selected gender.", icon: "error" });
        setLoading(false);
        return;
      }

      const isCityZipValid = await validateCityZip(form.city, form.zip_code);
      if (!isCityZipValid) {
        Swal.fire({ title: "Invalid Location", text: "City and Zip Code do not match.", icon: "error" });
        setLoading(false);
        return;
      }

      const isCityDistrictValid = await validateCityDistrict(form.city, Number(form.district_id));
      if (!isCityDistrictValid) {
        Swal.fire({ title: "Invalid District", text: "City does not belong to the selected district.", icon: "error" });
        setLoading(false);
        return;
      }

      const fd = new FormData();
      Object.keys(form).forEach((key) => fd.append(key, form[key]));
      fd.append("nic_front", nicFront);
      fd.append("nic_back", nicBack);
      fd.append("water_bill_front", waterFront);
      fd.append("water_bill_back", waterBack);

      if (businessDoc) {
        fd.append("business_registration_or_qualifications", businessDoc);
      }

      const res = await axios.post(`${API_URL}/agents/register-guide`, fd, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (res.status === 201) {
        Swal.fire({
          title: "Registration Success!",
          text: "Guide verification email sent.",
          icon: "success",
          confirmButtonColor: "#3b82f6"
        }).then(() => {
          navigate("/success?status=pending&message=Guide+verification+email+sent");
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error", text: "Error submitting guide registration.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Guide Partnership</h1>
          <p>Register new local guides to provide expert experiences on Soloway.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        <div className="premium-glass-card">
          <div className="card-header-premium">
            <button onClick={() => navigate(-1)} className="btn-back-minimal">
              <FaArrowLeft />
            </button>
            <div className="header-titles">
              <h2>Register New Guide</h2>
              <p>Onboard a professional travel guide</p>
            </div>
          </div>

          {!isAgentApproved && (
            <div style={{
              background: "rgba(245, 158, 11, 0.15)",
              border: "1px solid #f59e0b",
              color: "#d97706",
              padding: "16px 20px",
              borderRadius: "12px",
              marginBottom: "25px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontWeight: "600"
            }}>
              <span style={{ fontSize: "24px" }}>⏳</span>
              <span>{approvalMessage || "Your Travel Agent account is pending administrator approval. You cannot register guides yet."}</span>
            </div>
          )}

          <form className="premium-form-layout" onSubmit={handleSubmit}>
            <div className="form-grid-2">
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaUser /> <span>Personal Information</span>
                </div>
                
                <div className="premium-field">
                  <label>Full Name</label>
                  <input name="full_name" value={form.full_name} onChange={handleChange} placeholder="Guide's Legal Name" required />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Email</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="guide@example.com" required />
                  </div>
                  <div className="premium-field">
                    <label>Phone Number</label>
                    <input name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="+94..." required />
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>National ID Number</label>
                    <input name="national_id_number" value={form.national_id_number} onChange={handleChange} placeholder="ID or Passport" required />
                  </div>
                  <div className="premium-field">
                    <label>Date of Birth</label>
                    <input type="date" name="dob" value={form.dob} onChange={handleChange} required />
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Gender</label>
                    <select name="gender_id" value={form.gender_id} onChange={handleChange} required>
                      <option value="">Select</option>
                      {genders.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                  <div className="premium-field">
                    <label>Nationality</label>
                    <select name="nationality_id" value={form.nationality_id} onChange={handleChange} required>
                      <option value="">Select</option>
                      {nationalities.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="section-header-minimal" style={{ marginTop: '30px' }}>
                  <FaMapMarkerAlt /> <span>Location Details</span>
                </div>

                <div className="premium-field">
                  <label>Full Address</label>
                  <input name="address" value={form.address} onChange={handleChange} placeholder="Street, Building" required />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>District</label>
                    <select name="district_id" value={form.district_id} onChange={handleChange} required>
                      <option value="">Select District</option>
                      {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="premium-field">
                    <label>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="City" required />
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Zip Code</label>
                    <input name="zip_code" value={form.zip_code} onChange={handleChange} placeholder="Zip" required />
                  </div>
                  <div className="premium-field">
                    <label>Country</label>
                    <select name="country_id" value={form.country_id} onChange={handleChange} required>
                      <option value="">Select</option>
                      {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-column">
                <div className="section-header-minimal">
                  <FaFileAlt /> <span>Verification Documents</span>
                </div>

                <div className="upload-grid-premium">
                  <div className="premium-upload-item">
                    <label>Guide ID Front</label>
                    <label className={`upload-zone ${nicFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setNicFront)} required />
                      <FaCloudUploadAlt />
                      <span>{nicFront ? nicFront.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Guide ID Back</label>
                    <label className={`upload-zone ${nicBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setNicBack)} required />
                      <FaCloudUploadAlt />
                      <span>{nicBack ? nicBack.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Utility Bill (Front)</label>
                    <label className={`upload-zone ${waterFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setWaterFront)} required />
                      <FaCloudUploadAlt />
                      <span>{waterFront ? waterFront.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Utility Bill (Back)</label>
                    <label className={`upload-zone ${waterBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setWaterBack)} required />
                      <FaCloudUploadAlt />
                      <span>{waterBack ? waterBack.name : "Choose File"}</span>
                    </label>
                  </div>
                </div>

                <div className="premium-field full-width" style={{ marginTop: '20px' }}>
                  <label>Professional Qualifications (Optional)</label>
                  <label className={`upload-zone-wide ${businessDoc ? 'active' : ''}`}>
                    <input type="file" onChange={handleFile(setBusinessDoc)} />
                    <FaCloudUploadAlt />
                    <span>{businessDoc ? businessDoc.name : "Upload certifications or registration"}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="action-row-centered">
              <button type="submit" className="btn-premium-action" disabled={loading}>
                {loading ? (
                  <span className="loader-dots">Processing...</span>
                ) : (
                  <>
                    <FaCheckCircle /> Register Guide
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AgentGuideRegister;
